import React from 'react';
import { DownloadIcon, CheckCircleIcon, EuroIcon, FileTextIcon, PlusCircleIcon, ShieldIcon } from 'lucide-react';
import { FadeIn } from '../components/FadeIn';
import { SectionHeading } from '../components/SectionHeading';
import { PageHero } from '../components/PageHero';
import { CTASection } from '../components/CTASection';
import { Button } from '../components/Button';
interface KostenPageProps {
  onNavigate: (page: string) => void;
}
export function KostenPage({
  onNavigate
}: KostenPageProps) {
  const hypotheekTarieven = [{
    title: 'Starter',
    price: '2.750',
    description: 'Je koopt je eerste woning'
  }, {
    title: 'Doorstromer',
    price: '3.250',
    description: 'Je verkoopt je huidige woning en koopt een nieuwe'
  }, {
    title: 'Starter + ondernemer',
    price: '3.250',
    description: 'Je koopt je eerste woning als ondernemer'
  }, {
    title: 'Doorstromer + ondernemer',
    price: '3.500',
    description: 'Je verkoopt en koopt als ondernemer'
  }, {
    title: 'Woonboot',
    price: '3.500',
    description: 'Je koopt of herfinanciert een woonboot'
  }, {
    title: 'Scheiding',
    price: '3.500',
    description: 'Je wilt de hypotheek verdelen na een scheiding'
  }, {
    title: '2e hypotheek',
    price: '2.250',
    description: 'Je wilt je bestaande hypotheek verhogen'
  }, {
    title: 'Maatwerkhypotheek',
    price: '3.950',
    description: 'Complexe situatie die extra aandacht vraagt'
  }];
  return <div className="w-full" data-id="element-619">
      {/* Hero Section */}
      <PageHero title="Helder over kosten" subtitle="Je weet vooraf precies waar je aan toe bent. Geen verborgen kosten, geen provisies, gewoon helder advies voor een vast tarief." label="Kosten" data-id="element-620" />

      {/* Pricing Explanation */}
      <section className="py-12 md:py-28 bg-white" data-id="element-621">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-622">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12" data-id="element-623">
            <FadeIn className="w-full md:w-1/2" data-id="element-624">
              <h3 className="text-2xl font-bold text-navy mb-6" data-id="element-625">
                Hoe wij werken
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-626">
                Wij werken niet op basis van provisie van banken of
                geldverstrekkers, maar met een vast adviestarief dat wij vooraf
                met je afspreken.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-627">
                <li className="flex items-start" data-id="element-628">
                  <CheckCircleIcon className="w-6 h-6 text-gold mr-3 flex-shrink-0 mt-0.5" data-id="element-629" />
                  <span className="text-gray-700 font-medium" data-id="element-630">
                    Het eerste kennismakingsgesprek is altijd gratis en
                    vrijblijvend.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-631">
                  <CheckCircleIcon className="w-6 h-6 text-gold mr-3 flex-shrink-0 mt-0.5" data-id="element-632" />
                  <span className="text-gray-700 font-medium" data-id="element-633">
                    Je ontvangt vooraf een duidelijke offerte met het vaste
                    tarief.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-634">
                  <CheckCircleIcon className="w-6 h-6 text-gold mr-3 flex-shrink-0 mt-0.5" data-id="element-635" />
                  <span className="text-gray-700 font-medium" data-id="element-636">
                    Geen uurtje-factuurtje, maar een all-in prijs voor het
                    complete traject.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-637">
                  <CheckCircleIcon className="w-6 h-6 text-gold mr-3 flex-shrink-0 mt-0.5" data-id="element-638" />
                  <span className="text-gray-700 font-medium" data-id="element-639">
                    Kosten voor hypotheekadvies zijn vaak fiscaal aftrekbaar.
                  </span>
                </li>
              </ul>
            </FadeIn>
            <FadeIn delay={0.2} className="w-full md:w-1/2" data-id="element-640">
              <div className="bg-navy p-6 md:p-10 rounded-2xl shadow-card-lg text-white" data-id="element-641">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-white/10 rounded-full flex items-center justify-center mb-6" data-id="element-642">
                  <EuroIcon className="w-7 h-7 text-gold" data-id="element-643" />
                </div>
                <h3 className="text-2xl font-bold mb-4" data-id="element-644">
                  Transparante tarieven
                </h3>
                <p className="text-gray-300 leading-relaxed mb-6" data-id="element-645">
                  De kosten voor ons advies hangen af van jouw specifieke
                  situatie. Omdat wij geloven in transparantie, vind je
                  hieronder een helder overzicht van onze vaste tarieven.
                </p>
                <p className="text-gray-300 leading-relaxed mb-8" data-id="element-646">
                  Tijdens het gratis kennismakingsgesprek brengen wij jouw
                  situatie in kaart en bevestigen we direct welk tarief van
                  toepassing is.
                </p>
                <Button variant="primary" size="lg" onClick={() => onNavigate('contact')} className="w-full" data-id="element-647">
                  Plan gratis kennismaking
                </Button>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Hypotheekadvies Tarieven Grid */}
      <section className="py-12 md:py-28 bg-warm" data-id="element-648">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-649">
          <SectionHeading title="Tarieven hypotheekadvies" subtitle="Een vast bedrag voor het complete traject: van oriëntatie tot en met het passeren bij de notaris." data-id="element-650" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-8 md:mt-12" data-id="element-651">
            {hypotheekTarieven.map((tarief, index) => <FadeIn key={index} delay={Math.min(index, 3) * 0.08} className="bg-white p-5 md:p-6 rounded-2xl shadow-card border border-gray-100 flex sm:flex-col items-center sm:items-start justify-between sm:justify-start hover:-translate-y-1 transition-transform duration-300 gap-4 sm:gap-0" data-id="element-652">
                <div className="mb-0 sm:mb-4 flex-grow" data-id="element-653">
                  <h4 className="text-base md:text-lg font-bold text-navy mb-1 md:mb-2" data-id="element-654">
                    {tarief.title}
                  </h4>
                  <p className="text-xs md:text-sm text-gray-500 line-clamp-2 sm:line-clamp-none" data-id="element-655">
                    {tarief.description}
                  </p>
                </div>
                <div className="mt-0 sm:mt-auto pt-0 sm:pt-4 border-t-0 sm:border-t border-gray-50 flex-shrink-0 text-right sm:text-left w-24 sm:w-full" data-id="element-656">
                  <div className="flex items-baseline justify-end sm:justify-start text-navy" data-id="element-657">
                    <span className="text-lg md:text-xl font-bold mr-1" data-id="element-658">€</span>
                    <span className="text-2xl md:text-3xl font-extrabold text-gold" data-id="element-659">
                      {tarief.price}
                    </span>
                  </div>
                </div>
              </FadeIn>)}
          </div>

          <FadeIn delay={0.4} className="mt-8 text-center" data-id="element-660">
            <p className="text-sm text-gray-500 italic" data-id="element-661">
              Alle genoemde bedragen zijn inclusief btw.
            </p>
          </FadeIn>

          {/* Toeslagen */}
          <FadeIn delay={0.5} className="mt-8 md:mt-12 max-w-3xl mx-auto" data-id="element-662">
            <div className="bg-white p-5 md:p-6 rounded-2xl shadow-sm border border-gold/20 flex flex-col sm:flex-row items-start gap-4" data-id="element-663">
              <div className="w-10 h-10 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0 sm:mt-1" data-id="element-664">
                <PlusCircleIcon className="w-5 h-5 text-gold" data-id="element-665" />
              </div>
              <div data-id="element-666">
                <h4 className="text-lg font-bold text-navy mb-2" data-id="element-667">
                  Eventuele toeslagen
                </h4>
                <p className="text-gray-600" data-id="element-668">
                  Voor specifieke extra werkzaamheden rekenen wij een opslag van{' '}
                  <strong className="text-navy" data-id="element-669">€ 250</strong> bovenop het
                  basistarief. Dit geldt voor:
                </p>
                <ul className="mt-3 space-y-1 text-gray-600 list-disc list-inside" data-id="element-670">
                  <li data-id="element-671">Meetekenen van ouders</li>
                  <li data-id="element-672">Aanvragen van een starterslening</li>
                  <li data-id="element-673">Opstellen van een draagplichtovereenkomst</li>
                </ul>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Verzekeringen Tarieven */}
      <section className="py-12 md:py-28 bg-white" data-id="element-674">
        <div className="max-w-5xl mx-auto px-4 md:px-8" data-id="element-675">
          <SectionHeading title="Tarieven verzekeringen" subtitle="Naast je hypotheek adviseren wij ook over het afdekken van financiële risico's." data-id="element-676" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-8 md:mt-12" data-id="element-677">
            <FadeIn delay={0.1} className="bg-warm p-6 md:p-8 rounded-2xl shadow-card border border-gray-100 flex flex-col" data-id="element-678">
              <div className="flex items-center gap-4 mb-4 md:mb-6" data-id="element-679">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-680">
                  <ShieldIcon className="w-7 h-7 text-gold" data-id="element-681" />
                </div>
                <h3 className="text-xl font-bold text-navy" data-id="element-682">
                  Overlijdensrisico-
                  <br data-id="element-683" />
                  verzekering
                </h3>
              </div>
              <p className="text-gray-600 mb-8 flex-grow" data-id="element-684">
                Advies en bemiddeling voor een overlijdensrisicoverzekering om
                je nabestaanden financieel te beschermen.
              </p>
              <div className="pt-4 border-t border-gray-200" data-id="element-685">
                <div className="flex items-baseline text-navy" data-id="element-686">
                  <span className="text-xl font-bold mr-1" data-id="element-687">€</span>
                  <span className="text-4xl font-extrabold text-gold" data-id="element-688">250</span>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-warm p-6 md:p-8 rounded-2xl shadow-card border border-gray-100 flex flex-col" data-id="element-689">
              <div className="flex items-center gap-4 mb-4 md:mb-6" data-id="element-690">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-691">
                  <ShieldIcon className="w-7 h-7 text-gold" data-id="element-692" />
                </div>
                <h3 className="text-xl font-bold text-navy" data-id="element-693">
                  Arbeidsongeschiktheids-
                  <br data-id="element-694" />
                  verzekering (AOV)
                </h3>
              </div>
              <p className="text-gray-600 mb-8 flex-grow" data-id="element-695">
                Uitgebreid advies en bemiddeling voor inkomensbescherming bij
                ziekte of arbeidsongeschiktheid.
              </p>
              <div className="pt-4 border-t border-gray-200" data-id="element-696">
                <div className="flex items-baseline text-navy" data-id="element-697">
                  <span className="text-xl font-bold mr-1" data-id="element-698">€</span>
                  <span className="text-4xl font-extrabold text-gold" data-id="element-699">500</span>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Vergelijkingskaarten */}
      <section className="py-12 md:py-28 bg-navy text-white" data-id="element-700">
        <div className="max-w-5xl mx-auto px-4 md:px-8" data-id="element-701">
          <SectionHeading title="Vergelijkingskaarten" subtitle="Als financieel dienstverlener zijn wij wettelijk verplicht om je vooraf te informeren over onze dienstverlening en kosten. Dit doen wij via de standaard Vergelijkingskaarten opgesteld door de AFM." light data-id="element-702" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-8 md:mt-12" data-id="element-703">
            <FadeIn delay={0.1} className="bg-white/5 p-6 md:p-8 rounded-2xl border border-white/10 flex flex-col h-full" data-id="element-704">
              <div className="flex items-center gap-4 mb-4 md:mb-6" data-id="element-705">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-706">
                  <FileTextIcon className="w-7 h-7 text-gold" data-id="element-707" />
                </div>
                <h3 className="text-xl font-bold text-white" data-id="element-708">
                  Vergelijkingskaart Vermogen opbouwen
                </h3>
              </div>
              <p className="text-gray-300 mb-8 flex-grow" data-id="element-709">
                In dit document lees je wat wij voor je kunnen betekenen bij het
                opbouwen van vermogen en wat de gemiddelde kosten hiervoor zijn.
              </p>
              <Button variant="primary" href="https://drive.google.com/file/d/1b8FvwxU2Y9PF0TaqQzvJqaRWdo7bIagE/" icon={<DownloadIcon className="w-5 h-5" data-id="element-711" />} iconPosition="left" className="w-full" data-id="element-710">
                Download PDF
              </Button>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-white/5 p-6 md:p-8 rounded-2xl border border-white/10 flex flex-col h-full" data-id="element-712">
              <div className="flex items-center gap-4 mb-4 md:mb-6" data-id="element-713">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-714">
                  <FileTextIcon className="w-7 h-7 text-gold" data-id="element-715" />
                </div>
                <h3 className="text-xl font-bold text-white" data-id="element-716">
                  Vergelijkingskaart Hypotheek
                </h3>
              </div>
              <p className="text-gray-300 mb-8 flex-grow" data-id="element-717">
                In dit document lees je wat wij voor je kunnen betekenen op het
                gebied van hypotheken en wat de gemiddelde kosten hiervoor zijn.
              </p>
              <Button variant="primary" href="https://drive.google.com/file/d/1w8d3v2y09IYEr-81wYVKgw4tzT1S25aa/" icon={<DownloadIcon className="w-5 h-5" data-id="element-719" />} iconPosition="left" className="w-full" data-id="element-718">
                Download PDF
              </Button>
            </FadeIn>

            <FadeIn delay={0.3} className="bg-white/5 p-6 md:p-8 rounded-2xl border border-white/10 flex flex-col h-full" data-id="element-720">
              <div className="flex items-center gap-4 mb-4 md:mb-6" data-id="element-721">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-722">
                  <FileTextIcon className="w-7 h-7 text-gold" data-id="element-723" />
                </div>
                <h3 className="text-xl font-bold text-white" data-id="element-724">
                  Vergelijkingskaart Risico's afdekken
                </h3>
              </div>
              <p className="text-gray-300 mb-8 flex-grow" data-id="element-725">
                In dit document lees je wat wij voor je kunnen betekenen op het
                gebied van levens- en schadeverzekeringen en de bijbehorende
                kosten.
              </p>
              <Button variant="primary" href="https://drive.google.com/file/d/1r-5ZvxILzRhR3rJ4CvLP2HSxFJVqNjHv/" icon={<DownloadIcon className="w-5 h-5" data-id="element-727" />} iconPosition="left" className="w-full" data-id="element-726">
                Download PDF
              </Button>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection title="Vragen over de kosten?" subtitle="Wij leggen het je graag persoonlijk uit. Neem gerust contact op voor meer informatie." buttonText="Plan een gratis kennismaking" onNavigate={onNavigate} page="contact" variant="light" data-id="element-728" />
    </div>;
}